import { Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BreadCrumb } from '@nationwide/internet-servicing-angular-components';
import { environment } from 'environments/environment';
import { commercialConstants } from '../../../common/commercial-constants';
import { LoggerService } from '../../../shared/services/logger.service';
import { ClaimsDataService } from '../../claims/services/claims-data.service';
import { OmsService } from '../../claims/services/oms.service';
import { CommercialLoggingService } from '../services/commercial-logging.service';
import { SubmissionFailedComponent } from './submission-failed/submission-failed.component';
@Component({
    selector: 'is-first-notice-of-loss',
    templateUrl: './first-notice-of-loss.component.html',
    styleUrls: ['./first-notice-of-loss.component.scss'],
    standalone: false
})
export class FirstNoticeOfLossComponent implements OnInit {
    breadCrumbs: BreadCrumb[];
    contactPhoneNumber: UntypedFormControl = new UntypedFormControl('', [Validators.required,
    Validators.pattern(/^[1-9]\d{2}-\d{3}-\d{4}$/)]);
    currentPage = 'Start a Claim';
    dateOfIncident: UntypedFormControl = new UntypedFormControl('', [Validators.required]);
    description: UntypedFormControl = new UntypedFormControl('', Validators.required);
    // eslint-disable-next-line no-magic-numbers
    descriptionMaxLength = 1000;
    fnolForm: UntypedFormGroup;
    homeIconLink = environment.NATIONWIDE.accountSummaryPage;
    hours = commercialConstants.hours;
    incidentHour: UntypedFormControl = new UntypedFormControl('', Validators.required);
    incidentLocationState: string;
    incidentMeridiem: UntypedFormControl = new UntypedFormControl('AM');
    incidentMinute: UntypedFormControl = new UntypedFormControl('', Validators.required);
    lineOfBusiness: string;
    minutes = commercialConstants.minutes;
    namedInsured: string;
    state: string;
    // eslint-disable-next-line @typescript-eslint/member-ordering, no-magic-numbers
    maxDate = new Date().toISOString().slice(0, -14);
    states = commercialConstants.states;
    submitterEmail: UntypedFormControl = new UntypedFormControl('', [Validators.required,
    Validators.pattern(commercialConstants.regexValidators.isValidEmail)]);
    submitterFirstName: UntypedFormControl = new UntypedFormControl('', [Validators.required,
    Validators.pattern(commercialConstants.regexValidators.nameField)]);
    submitterLastName: UntypedFormControl = new UntypedFormControl('', [Validators.required,
    Validators.pattern(commercialConstants.regexValidators.nameField)]);
    submitterPhoneType: UntypedFormControl = new UntypedFormControl('', Validators.required);
    submitterRecieveText: UntypedFormControl = new UntypedFormControl({ value: '', disabled: true });
    timeOfIncident: UntypedFormGroup;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private LOGGER: LoggerService,
        private router: Router,
        public claimsDataService: ClaimsDataService,
        private omsService: OmsService,
        private dialog: MatDialog,
        private commercialLoggingService: CommercialLoggingService
    ) {
    }
    buildForm(): void {
        this.timeOfIncident = this.formBuilder.group(
            {
                incidentHour: this.incidentHour,
                incidentMinute: this.incidentMinute
            }
        );
        this.fnolForm = this.formBuilder.group({
            submitterFirstName: this.submitterFirstName,
            description: this.description,
            incidentMeridiem: this.incidentMeridiem,
            timeOfIncident: this.timeOfIncident,
            submitterPhoneType: this.submitterPhoneType,
            submitterEmail: this.submitterEmail,
            submitterLastName: this.submitterLastName,
            contactPhoneNumber: this.contactPhoneNumber,
            dateOfIncident: this.dateOfIncident,
            submitterRecieveText: this.submitterRecieveText
        });
    }

    cancelClaim(): void {
        this.commercialLoggingService.logButtonAction(this.LOGGER, this.currentPage, 'cancel-claim', 'success', this.getLogMetadata());
        this.commercialLoggingService.updatePreviousUrlContext();
        this.router.navigate([commercialConstants.URLS.pakClaimsList]);
    }

    ngOnInit(): void {
        const shortWait = 100;
        this.breadCrumbs = [
            {
                name: 'Package (PAK) Claims',
                link: `${commercialConstants.URLS.pakClaimsList}`
            }, {
                name: 'Policy Claims List',
                link: `${commercialConstants.URLS.policyClaimsList}`
            }
        ];
        this.lineOfBusiness = this.claimsDataService.getFnolFlow();
        this.namedInsured = this.claimsDataService.getClaimsPackage() ?
            this.claimsDataService.getClaimsPackage()?.policyAccount.namedInsuredName : '';
        if (this.claimsDataService.getClaimsPackage()) {
            this.state = this.claimsDataService.getPrimary(this.claimsDataService.getClaimsPackage().address).state;
        }
        window.scrollTo(0, 0);
        this.commercialLoggingService.logPageLoad(this.LOGGER, this.currentPage, this.getLogMetadata());
        this.commercialLoggingService.updatePreviousUrlContext();
        this.buildForm();
        this.submitterPhoneType.valueChanges.subscribe((value) => {
            if (value === 'Cell' && this.lineOfBusiness === 'Commercial Auto') {
                this.submitterRecieveText.setValidators(Validators.required);
                this.submitterRecieveText.markAsUntouched();
                this.submitterRecieveText.enable();
            } else {
                this.submitterRecieveText.disable();
            }
        });

        setTimeout(() => {
            const addressStateControl = this.fnolForm.get('addressGroup')?.get('state');
            if (addressStateControl) {
                addressStateControl.valueChanges.subscribe((stateValue) => {
                    this.incidentLocationState = stateValue || '';
                });
            }
        }, shortWait);
    }

    openDialog(): void {
        let dialogConfig = new MatDialogConfig();
        dialogConfig = {
            disableClose: true,
            width: '450px',
            data: {
                description: 'Sorry, we’re unable to process your request at this time. Please call 1-800-421-3535 to file your claim.'
            }
        };
        this.dialog.open(SubmissionFailedComponent, dialogConfig);
    }

    submitForm(): void {
        if (this.fnolForm.valid) {
            const templateBody = this.omsService.buildTemplateContent(this.fnolForm.value, this.namedInsured);
            this.fnolForm.disable();
            this.omsService.sendEmail(templateBody, this.claimsDataService.getFnolFlow()).subscribe((response) => {
                this.commercialLoggingService.logServiceSuccess(
                    this.LOGGER,
                    this.currentPage,
                    'OmsService',
                    'sendEmail',
                    this.getLogMetadata({ claimNumber: this.claimsDataService.getClaimNumber() })
                );
                this.commercialLoggingService.logButtonAction(
                    this.LOGGER,
                    this.currentPage,
                    'submit-claim',
                    'success',
                    this.getLogMetadata({ claimNumber: this.claimsDataService.getClaimNumber() })
                );
                this.commercialLoggingService.updatePreviousUrlContext();
                this.router.navigate([commercialConstants.URLS.claimsConfirmation]);
                this.fnolForm.reset();
            }, (error) => {
                this.openDialog();
                this.commercialLoggingService.logServiceFailure(
                    this.LOGGER,
                    this.currentPage,
                    'OmsService',
                    'sendEmail',
                    this.getLogMetadata({ claimNumber: this.claimsDataService.getClaimNumber() }),
                    error
                );
                this.commercialLoggingService.logButtonAction(
                    this.LOGGER,
                    this.currentPage,
                    'submit-claim',
                    'failure',
                    this.getLogMetadata({ claimNumber: this.claimsDataService.getClaimNumber() })
                );
            });
        } else {
            this.commercialLoggingService.logButtonAction(
                this.LOGGER,
                this.currentPage,
                'submit-claim',
                'invalid',
                this.getLogMetadata({ claimNumber: this.claimsDataService.getClaimNumber() })
            );
            window.scrollTo(0, 0);
            this.validateAllFormFields(this.fnolForm);
        }
    }

    validateAllFormFields(formGroup: UntypedFormGroup): any {
        for (const field of Object.keys(formGroup.controls)) {
            const control = formGroup.get(field);
            if (control instanceof UntypedFormControl) {
                control.markAsTouched({ onlySelf: true });
            } else if (control instanceof UntypedFormGroup) {
                this.validateAllFormFields(control);
            } else if (control instanceof UntypedFormArray) {
                control.markAsTouched({ onlySelf: true });
            }
        }
    }

    private getLogMetadata(additionalFields: any = {}): any {
        return {
            policyNumber: this.claimsDataService.getClaimsPolicyNumber(),
            policyType: this.claimsDataService.getClaimsPolicyType(),
            lineOfBusiness: this.claimsDataService.getFnolFlow(),
            claimsDisplayCount: this.claimsDataService.getClaimsDisplayCount() || 'UNKNOWN',
            alphaState: this.claimsDataService.getPrimary(this.claimsDataService.getClaimsPackage()?.address)?.state || 'UNKNOWN',
            previousUrl: window.sessionStorage.getItem('previousUrl') || '',
            ...additionalFields
        };
    }
}
